import * as Yup from 'yup';

import { makeLabels } from 'utils/i18n';

import { BridgeDocumentPurpose, IdentifyingInformationType, PersonAddressType } from '../kyb/types';

const NAMESPACE = 'page.merchant.sections.associatedPerson';

export type AssociatedPersonDocumentType = {
  purposes?: BridgeDocumentPurpose[];
  file?: File | string;
  description?: string;
};

export type AssociatedPersonFormType = {
  uuid?: string;
  kycScreen?: { screenedAt: string; result: string };
  ofacScreen?: { screenedAt: string; result: string };
  firstName: string;
  lastName: string;
  email: string;
  residentialAddress: PersonAddressType;
  birthDate: string | null;
  hasOwnership: boolean;
  hasControl: boolean;
  isSigner: boolean;
  phone?: string;
  isDirector: boolean;
  title: string;
  ownershipPercentage: number | undefined;
  relationshipEstablishedAt?: string;
  identifyingInformation: IdentifyingInformationType[];
  documents: AssociatedPersonDocumentType[];
};

export type AssociatedPersonsFormType = {
  uuid?: string;
  beneficiaries: AssociatedPersonFormType[];
};

export const ASSOCIATED_PERSON_INITIAL_VALUES: AssociatedPersonFormType = {
  kycScreen: { screenedAt: '', result: 'passed' },
  ofacScreen: { screenedAt: '', result: 'passed' },
  firstName: '',
  lastName: '',
  email: '',
  residentialAddress: {
    streetLine1: '',
    streetLine2: undefined,
    city: '',
    subdivision: undefined,
    postalCode: '',
    country: '',
  },
  birthDate: null,
  hasOwnership: false,
  hasControl: true,
  isSigner: true,
  identifyingInformation: [{ type: '', issuingCountry: '', number: '' }],
  documents: [
    {
      purposes: [],
      file: undefined,
      description: undefined,
    },
  ],
  phone: undefined,
  isDirector: false,
  title: '',
  ownershipPercentage: 0,
  relationshipEstablishedAt: undefined,
};

export const ASSOCIATED_PERSONS_INITIAL_VALUES: AssociatedPersonsFormType = {
  beneficiaries: [{ ...ASSOCIATED_PERSON_INITIAL_VALUES }],
};

export const KEYS = {
  STORAGE_KEY: 'associatedPersonFormData',
  MODAL: 'associatedPerson',
};

export type ModalKey = (typeof KEYS)['MODAL'];

const dateFormatRegex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;
const dateFormatMessage = 'Is not in correct format,\n ex. : "1980-03-24" (March 24, 1980)';

const requiredDateSchema = (fieldRequiredMessage: string) =>
  Yup.string().trim().matches(dateFormatRegex, dateFormatMessage).required(fieldRequiredMessage);

const optionalDateSchema = Yup.string().trim().matches(dateFormatRegex, dateFormatMessage).nullable().optional();

const requiredBirthDateSchema = (fieldRequiredMessage: string) =>
  requiredDateSchema(fieldRequiredMessage).test('birth-date-age-range', 'Age must be between 10 and 124 years', (value) => {
    if (!value) return true;
    const today = new Date();
    const birth = new Date(value);
    const minDate = new Date(today.getFullYear() - 124, today.getMonth(), today.getDate());
    const maxDate = new Date(today.getFullYear() - 10, today.getMonth(), today.getDate());
    return birth >= minDate && birth <= maxDate;
  });

const optionalBirthDateSchema = optionalDateSchema.test('birth-date-age-range', 'Age must be between 10 and 124 years', (value) => {
  if (!value) return true;
  const today = new Date();
  const birth = new Date(value);
  const minDate = new Date(today.getFullYear() - 124, today.getMonth(), today.getDate());
  const maxDate = new Date(today.getFullYear() - 10, today.getMonth(), today.getDate());
  return birth >= minDate && birth <= maxDate;
});

const relationshipEstablishedAtSchema = optionalDateSchema.test(
  'relationship-date-range',
  'Date must be between 1976-01-01 and today',
  (value) => {
    if (!value) return true;
    const date = new Date(value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date >= new Date('1976-01-01') && date <= today;
  },
);

const requiredIdentifyingInformationSchema = (fieldRequiredMessage: string) =>
  Yup.array()
    .of(
      Yup.object({
        type: Yup.string().min(1).required(fieldRequiredMessage),
        issuingCountry: Yup.string().min(1).required(fieldRequiredMessage),
        number: Yup.string().min(1).required(fieldRequiredMessage),
        imageFront: Yup.mixed().optional(),
      }),
    )
    .min(1, 'At least one identifying information is required')
    .required(fieldRequiredMessage);

const optionalIdentifyingInformationSchema = Yup.array()
  .of(
    Yup.object({
      type: Yup.string().optional(),
      issuingCountry: Yup.string().optional(),
      number: Yup.string().min(1).optional(),
      imageFront: Yup.mixed().optional(),
    }),
  )
  .optional();

const documentsSchema = Yup.array()
  .of(
    Yup.object({
      purposes: Yup.array().of(Yup.string()).optional(),
      file: Yup.mixed().optional(),
      description: Yup.string().optional(),
    }),
  )
  .optional();

export const createRequiredPersonAddressSchema = (fieldRequiredMessage: string) =>
  Yup.object().shape({
    streetLine1: Yup.string().min(1).required(fieldRequiredMessage),
    streetLine2: Yup.string().min(1).optional(),
    city: Yup.string().min(1).required(fieldRequiredMessage),
    subdivision: Yup.string().when('country', {
      is: 'USA',
      then: (schema) => schema.required(fieldRequiredMessage),
      otherwise: (schema) => schema.optional(),
    }),
    postalCode: Yup.string().min(1).matches(/^\d+$/, 'Postal code must contain digits only').required(fieldRequiredMessage),
    country: Yup.string().min(1).required(fieldRequiredMessage),
  });

export const createOptionalPersonAddressSchema = (fieldRequiredMessage: string) =>
  Yup.object().shape({
    streetLine1: Yup.string()
      .min(1)
      // eslint-disable-next-line func-names
      .test('required-when-address-provided', fieldRequiredMessage, function (value) {
        const { city } = this.parent;
        if (city && !value) {
          return false;
        }
        return true;
      }),
    streetLine2: Yup.string().min(1).optional(),
    city: Yup.string()
      .min(1)
      // eslint-disable-next-line func-names
      .test('required-when-address-provided', fieldRequiredMessage, function (value) {
        const { streetLine1 } = this.parent;
        if (streetLine1 && !value) {
          return false;
        }
        return true;
      }),
    subdivision: Yup.string().when('country', {
      is: 'USA',
      then: (schema) => schema.required(fieldRequiredMessage),
      otherwise: (schema) => schema.optional(),
    }),
    postalCode: Yup.string().min(1).matches(/^\d+$/, 'Postal code must contain digits only').optional(),
    country: Yup.string()
      .min(1)
      // eslint-disable-next-line func-names
      .test('required-when-address-provided', fieldRequiredMessage, function (value) {
        const { streetLine1, city } = this.parent;
        if ((streetLine1 || city) && !value) {
          return false;
        }
        return true;
      }),
  });

export const createNewAssociatedPersonSchema = (fieldRequiredMessage: string) =>
  Yup.object({
    uuid: Yup.string().optional(),
    kycScreen: Yup.object({
      screenedAt: Yup.string().required(fieldRequiredMessage),
      result: Yup.string().required(fieldRequiredMessage),
    }).required(fieldRequiredMessage),
    ofacScreen: Yup.object({
      screenedAt: Yup.string().required(fieldRequiredMessage),
      result: Yup.string().required(fieldRequiredMessage),
    }).required(fieldRequiredMessage),
    firstName: Yup.string().min(1).max(1024).required(fieldRequiredMessage),
    lastName: Yup.string().min(2).max(1024).required(fieldRequiredMessage),
    email: Yup.string().min(1).max(1024).email().required(fieldRequiredMessage),
    residentialAddress: createRequiredPersonAddressSchema(fieldRequiredMessage),
    documents: documentsSchema,
    identifyingInformation: requiredIdentifyingInformationSchema(fieldRequiredMessage),
    birthDate: requiredBirthDateSchema(fieldRequiredMessage),
    relationshipEstablishedAt: relationshipEstablishedAtSchema,
    phone: Yup.string().min(1).max(1024).optional(),
    hasOwnership: Yup.boolean().required(fieldRequiredMessage),
    hasControl: Yup.boolean().required(fieldRequiredMessage),
    isSigner: Yup.boolean().required(fieldRequiredMessage),
    isDirector: Yup.boolean().optional(),
    title: Yup.string().required(fieldRequiredMessage),
    ownershipPercentage: Yup.number()
      .typeError('Ownership percentage must be a number')
      .optional()
      .when('hasOwnership', {
        is: true,
        then: (schema) => schema.min(25, 'Associated person with ownership does not meet the business 25% ownership threshold'),
      }),
    ownershipValidation: Yup.boolean()
      .optional()
      .test(
        'at-least-one-true',
        'At least one of hasOwnership, hasControl, or isSigner must be true',
        // eslint-disable-next-line func-names
        function () {
          const { hasOwnership, hasControl, isSigner } = this.parent;
          return hasOwnership || hasControl || isSigner;
        },
      ),
  });

export const createUpdateAssociatedPersonSchema = (fieldRequiredMessage: string) =>
  Yup.object({
    uuid: Yup.string().required(),
    kycScreen: Yup.object({
      screenedAt: Yup.string().optional(),
      result: Yup.string().optional(),
    }).optional(),
    ofacScreen: Yup.object({
      screenedAt: Yup.string().optional(),
      result: Yup.string().optional(),
    }).optional(),
    firstName: Yup.string().min(1).max(1024).optional(),
    lastName: Yup.string().min(2).max(1024).optional(),
    email: Yup.string().min(1).max(1024).email().optional(),
    residentialAddress: createOptionalPersonAddressSchema(fieldRequiredMessage),
    documents: documentsSchema,
    identifyingInformation: optionalIdentifyingInformationSchema,
    birthDate: optionalBirthDateSchema,
    relationshipEstablishedAt: relationshipEstablishedAtSchema,
    phone: Yup.string().min(1).max(1024).optional(),
    hasOwnership: Yup.boolean().optional(),
    hasControl: Yup.boolean().optional(),
    isSigner: Yup.boolean().optional(),
    isDirector: Yup.boolean().optional(),
    title: Yup.string().optional(),
    ownershipPercentage: Yup.number()
      .typeError('Ownership percentage must be a number')
      .optional()
      .when('hasOwnership', {
        is: true,
        then: (schema) => schema.min(25, 'Associated person with ownership does not meet the business 25% ownership threshold'),
      }),
    ownershipValidation: Yup.boolean()
      .optional()
      .test(
        'at-least-one-true',
        'At least one of hasOwnership, hasControl, or isSigner must be true',
        // eslint-disable-next-line func-names
        function () {
          const { hasOwnership, hasControl, isSigner } = this.parent;
          if (hasOwnership === undefined && hasControl === undefined && isSigner === undefined) {
            return true;
          }
          return hasOwnership || hasControl || isSigner;
        },
      ),
  });

export const createAssociatedPersonSchema = (fieldRequiredMessage: string) =>
  Yup.lazy((value: AssociatedPersonFormType) => {
    if (value?.uuid) {
      return createUpdateAssociatedPersonSchema(fieldRequiredMessage);
    }
    return createNewAssociatedPersonSchema(fieldRequiredMessage);
  });

export const createAssociatedPersonsSchema = (fieldRequiredMessage: string) =>
  Yup.object({
    beneficiaries: Yup.array().of(createAssociatedPersonSchema(fieldRequiredMessage)).min(1),
  });

export const LABELS = makeLabels(
  {
    MODAL: {
      TITLE: ['modal.title', 'Associated Person'],
      CANCEL: ['modal.cancel', 'Cancel'],
      CONFIRM: ['modal.confirm', 'Confirm'],
    },
    TITAN_DATA_BUTTON: ['titanDataButton', 'Insert Titan Data'],
    LOADING: ['loading', 'Loading...'],
    PANEL: {
      TITLE: ['panel.title', 'Associated Persons'],
      ADD_BUTTON: ['panel.addButton', 'Add Associated Person'],
      EDIT_BUTTON: ['panel.editButton', 'Edit'],
      REMOVE_BUTTON: ['panel.removeButton', 'Remove'],
    },
    FORM: {
      TITLE: ['form.title', 'Associated Person such as Ultimate Beneficial Owner'],
      KYC_SCREEN: {
        TITLE: ['form.kycScreen.title', 'KYC Screening Date'],
      },
      OFAC_SCREEN: {
        TITLE: ['form.ofacScreen.title', 'OFAC Screening Date'],
      },
      FIRST_NAME: {
        TITLE: ['form.firstName.title', 'First Name'],
        PLACEHOLDER: ['form.firstName.placeholder', 'First Name'],
      },
      LAST_NAME: {
        TITLE: ['form.lastName.title', 'Last Name'],
        PLACEHOLDER: ['form.lastName.placeholder', 'Last Name'],
      },
      EMAIL: {
        TITLE: ['form.email.title', 'Email'],
        PLACEHOLDER: ['form.email.placeholder', 'Email'],
      },
      BIRTH_DATE: {
        TITLE: ['form.birthDate.title', 'Birth Date'],
        PLACEHOLDER: ['form.birthDate.placeholder', 'YYYY-MM-DD'],
      },
      PHONE: {
        TITLE: ['form.phone.title', 'Phone'],
        PLACEHOLDER: ['form.phone.placeholder', 'Phone'],
      },
      TITLE_FIELD: {
        TITLE: ['form.title.title', 'Title'],
        PLACEHOLDER: ['form.title.placeholder', 'Title'],
      },
      OWNERSHIP_PERCENTAGE: {
        TITLE: ['form.ownershipPercentage.title', 'Ownership Percentage'],
        PLACEHOLDER: ['form.ownershipPercentage.placeholder', '0'],
      },
      RELATIONSHIP_AT: {
        TITLE: ['form.relationshipAt.title', 'Relationship Established At'],
        PLACEHOLDER: ['form.relationshipAt.placeholder', 'YYYY-MM-DD'],
      },
      HAS_OWNERSHIP: {
        LABEL: ['form.hasOwnership.label', 'Has Ownership (25%+)'],
      },
      HAS_CONTROL: {
        LABEL: ['form.hasControl.label', 'Has Control'],
      },
      IS_SIGNER: {
        LABEL: ['form.isSigner.label', 'Is Signer'],
      },
      IS_DIRECTOR: {
        LABEL: ['form.isDirector.label', 'Is Director'],
      },
      ADDRESS: {
        TITLE: ['form.address.title', 'Residential Address'],
      },
      FIELD_REQUIRED: ['form.fieldRequired', 'This field is required'],
    },
    SECTIONS: {
      IDENTIFYING_INFORMATION: {
        TITLE: ['sections.identifyingInformation.title', 'Identifying Information'],
      },
      DOCUMENTS: {
        TITLE: ['sections.documents.title', 'Documents'],
      },
    },
    BENEFICIARY: {
      ADD: ['beneficiary.add', 'Add ultimate beneficial owner'],
      REMOVE: ['beneficiary.remove', 'Delete beneficial owner'],
      TITLE: ['beneficiary.title', 'Beneficial Owner'],
    },
  },
  NAMESPACE,
);

export const FORM_FIELDS = {
  KYC_SCREEN: 'kycScreen',
  OFAC_SCREEN: 'ofacScreen',
  FIRST_NAME: 'firstName',
  LAST_NAME: 'lastName',
  EMAIL: 'email',
  BIRTH_DATE: 'birthDate',
  PHONE: 'phone',
  TITLE: 'title',
  OWNERSHIP_PERCENTAGE: 'ownershipPercentage',
  RELATIONSHIP_ESTABLISHED_AT: 'relationshipEstablishedAt',
  HAS_OWNERSHIP: 'hasOwnership',
  HAS_CONTROL: 'hasControl',
  IS_SIGNER: 'isSigner',
  IS_DIRECTOR: 'isDirector',
  RESIDENTIAL_ADDRESS: 'residentialAddress',
  IDENTIFYING_INFORMATION: 'identifyingInformation',
  DOCUMENTS: 'documents',
};
